// Cooldown utilisation panel — Phase 2A third slice.
//
// One row per tracked active CD. Shows "uses / theoretical max", a
// utilisation bar (red if low), and per-fight metadata (first-use time,
// longest gap) on the single-fight view. The all-bosses view drops the
// per-fight metadata since gaps between fights aren't meaningful.

import type { CooldownSummary, CooldownUsage } from '@shared/analysis';
import { t } from '@shared/i18n';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

const ICON_BASE = '/api/img/images/wow/icons/medium';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function fmtPct(p: number): string {
  if (!Number.isFinite(p)) return '–';
  return `${(p * 100).toFixed(0)}%`;
}

function fmtSec(s: number | null): string {
  if (s == null || !Number.isFinite(s)) return '–';
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

function fmtCdLabel(cdSec: number): string {
  if (cdSec >= 60) return `${Math.round(cdSec / 60)}min`;
  return `${cdSec}s`;
}

// Colour-flavour the utilisation cell: green ≥ 90%, neutral 60-89%,
// red below 60% (something the player is genuinely leaving on the table).
function utilFlavour(util: number, uses: number): string {
  if (uses === 0) return 'bad';
  if (util >= 0.9) return 'good';
  if (util < 0.6) return 'bad';
  return '';
}

function makeRow(cd: CooldownUsage, isAggregate: boolean): HTMLTableRowElement {
  const tr = document.createElement('tr');
  tr.className = 'cd-row';
  const pct = cd.utilisation * 100;
  // The first-use / longest-gap columns are meaningless once we sum across
  // multiple fights, so replace them with em-dash.
  const firstUse = isAggregate ? '–' : fmtSec(cd.firstUseSec);
  const longestGap = isAggregate ? '–' : fmtSec(cd.longestGapSec);
  tr.innerHTML = `
    <td class="ability">
      <a class="cd-link" target="_blank" rel="noopener" href="/api/wh/classic/spell=${cd.abilityID}">
        <span class="icon-slot"></span>
        <span class="cd-name">spell ${cd.abilityID}</span>
      </a>
    </td>
    <td class="num muted cd-cd">${escapeHtml(fmtCdLabel(cd.cdSec))}</td>
    <td class="num cd-uses">${cd.uses} <span class="muted small">/ ${cd.maxUses}</span></td>
    <td class="num ${utilFlavour(cd.utilisation, cd.uses)}">${fmtPct(cd.utilisation)}</td>
    <td class="bar-col"><div class="cd-bar" style="width:${Math.min(100, pct).toFixed(1)}%"></div></td>
    <td class="num muted">${firstUse}</td>
    <td class="num muted">${longestGap}</td>
  `;

  const link = tr.querySelector<HTMLAnchorElement>('a.cd-link');
  if (link) attachTooltip(link, 'spell', cd.abilityID);

  // Async wowhead upgrade — name + icon.
  void lookup('spell', cd.abilityID).then((r) => {
    if (r.name) {
      const n = tr.querySelector<HTMLSpanElement>('.cd-name');
      if (n) n.textContent = r.name;
    }
    const slot = tr.querySelector<HTMLSpanElement>('.icon-slot');
    if (!slot) return;
    const iconSrc = r.icon ? iconNameToUrl(r.icon) : `${ICON_BASE}/inv_misc_questionmark.jpg`;
    const img = document.createElement('img');
    img.className = 'icon';
    img.width = 20;
    img.height = 20;
    img.alt = '';
    img.src = iconSrc;
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
    slot.replaceChildren(img);
  });

  return tr;
}

export function renderCooldownPanel(
  cooldowns: CooldownSummary,
  container: HTMLElement,
  isAggregate: boolean,
): void {
  container.replaceChildren();

  const usedAtAll = cooldowns.cooldowns.some((c) => c.uses > 0);
  if (!usedAtAll) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent = t('cd.empty');
    container.appendChild(p);
    return;
  }

  const summary = document.createElement('p');
  summary.className = 'analysis-summary muted';
  const totalUses = cooldowns.cooldowns.reduce((acc, c) => acc + c.uses, 0);
  const totalMax = cooldowns.cooldowns.reduce((acc, c) => acc + c.maxUses, 0);
  const overall = totalMax > 0 ? totalUses / totalMax : 0;
  summary.textContent = t('cd.summary', {
    uses: totalUses,
    max: totalMax,
    pct: fmtPct(overall),
  });
  container.appendChild(summary);

  const table = document.createElement('table');
  table.className = 'cd-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('cd.col.ability')}</th>
        <th title="${escapeHtml(t('cd.col.cdTip'))}">${t('cd.col.cd')}</th>
        <th title="${escapeHtml(t('cd.col.usesTip'))}">${t('cd.col.uses')}</th>
        <th>${t('cd.col.util')}</th>
        <th class="bar-col"></th>
        <th title="${escapeHtml(t('cd.col.firstTip'))}">${t('cd.col.first')}</th>
        <th title="${escapeHtml(t('cd.col.gapTip'))}">${t('cd.col.gap')}</th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;
  for (const cd of cooldowns.cooldowns) {
    tbody.appendChild(makeRow(cd, isAggregate));
  }
  container.appendChild(table);
}
